import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { unitApi } from '../../services/api';
import { Unit } from '../../types';

const UnitList: React.FC = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [unitNumber, setUnitNumber] = useState('');
  const [buildingId, setBuildingId] = useState('1');
  const [floor, setFloor] = useState('1');
  const [area, setArea] = useState('1000');
  const [bedrooms, setBedrooms] = useState('2');
  const [bathrooms, setBathrooms] = useState('2');
  const [parkingSpots, setParkingSpots] = useState('1');
  const [formError, setFormError] = useState('');

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await unitApi.getAll();
      const data = response.data;
      setUnits(Array.isArray(data) ? data : data?.data || []);
    } catch (err: any) {
      setError('Failed to load units.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUnits(); }, [fetchUnits]);

  const openAddDialog = () => {
    setEditingUnit(null);
    setUnitNumber('');
    setBuildingId('1');
    setFloor('1');
    setArea('1000');
    setBedrooms('2');
    setBathrooms('2');
    setParkingSpots('1');
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (u: Unit) => {
    setEditingUnit(u);
    setUnitNumber(u.unitNumber);
    setBuildingId(String(u.buildingId));
    setFloor(String(u.floor));
    setArea(String(u.area));
    setBedrooms(String(u.bedrooms));
    setBathrooms(String(u.bathrooms));
    setParkingSpots(String(u.parkingSpots));
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!unitNumber.trim()) { setFormError('Unit number is required.'); return; }
    const payload = {
      buildingId: Number(buildingId),
      unitNumber: unitNumber.trim(),
      floor: Number(floor),
      area: Number(area),
      bedrooms: Number(bedrooms),
      bathrooms: Number(bathrooms),
      parkingSpots: Number(parkingSpots),
    };

    setSaving(true);
    setFormError('');
    try {
      if (editingUnit) {
        await unitApi.update(editingUnit.id, payload);
      } else {
        await unitApi.create(payload);
      }
      await fetchUnits();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save unit.');
    } finally {
      setSaving(false);
    }
  };

  const displayed = units.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Units</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Unit</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Unit #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Building</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Floor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Area (sqft)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Beds</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Baths</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Parking</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No units found.</Typography></TableCell></TableRow>
              ) : displayed.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{u.unitNumber}</Typography></TableCell>
                  <TableCell>{u.buildingName || `Bldg #${u.buildingId}`}</TableCell>
                  <TableCell>{u.floor}</TableCell>
                  <TableCell>{u.area.toLocaleString()}</TableCell>
                  <TableCell>{u.bedrooms}</TableCell>
                  <TableCell>{u.bathrooms}</TableCell>
                  <TableCell>{u.parkingSpots}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEditDialog(u)} color="primary"><Edit /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]} component="div" count={units.length}
            rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUnit ? 'Edit Unit' : 'Add Unit'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Unit Number" fullWidth margin="normal" value={unitNumber} onChange={(e) => setUnitNumber(e.target.value)} required disabled={saving} />
          <TextField label="Building ID" fullWidth margin="normal" value={buildingId} onChange={(e) => setBuildingId(e.target.value)} type="number" disabled={saving} />
          <TextField label="Floor" fullWidth margin="normal" value={floor} onChange={(e) => setFloor(e.target.value)} type="number" disabled={saving} />
          <TextField label="Area (sqft)" fullWidth margin="normal" value={area} onChange={(e) => setArea(e.target.value)} type="number" disabled={saving} />
          <TextField label="Bedrooms" fullWidth margin="normal" value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} type="number" disabled={saving} />
          <TextField label="Bathrooms" fullWidth margin="normal" value={bathrooms} onChange={(e) => setBathrooms(e.target.value)} type="number" disabled={saving} />
          <TextField label="Parking Spots" fullWidth margin="normal" value={parkingSpots} onChange={(e) => setParkingSpots(e.target.value)} type="number" disabled={saving} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnitList;
